import React from 'react';

const ConsultantAssessmentReport = ({ props }) => {
  const reportData = props?.report_data || {};
  const inventoryUser = props?.inventory_user || {};
  const snapshot = props?.assessment_snapshot || {};
  
  const readiness = reportData.readiness_score || {};
  const wealthGap = reportData.wealth_gap || {};
  const profitGap = reportData.profit_gap || {};
  const tasks = reportData.tasks || [];
  
  const formatCurrency = (value) => {
    if (!value && value !== 0) return '$0';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
  };
  
  const formatPercent = (value) => {
    if (!value && value !== 0) return '0%';
    return `${(value * 100).toFixed(1)}%`;
  };
  
  const getQuadrantLabel = (quadrant) => {
    switch (quadrant) {
      case 'best_zone':
        return { label: 'Best Zone', bg: 'bg-green-500', text: 'text-white' };
      case 'fix_owner':
        return { label: 'Fix the Owner', bg: 'bg-yellow-500', text: 'text-black' };
      case 'fix_business':
        return { label: 'Fix the Business', bg: 'bg-yellow-500', text: 'text-black' };
      case 'danger_zone':
        return { label: 'Danger Zone', bg: 'bg-red-500', text: 'text-white' };
      default:
        return { label: 'Not Available', bg: 'bg-gray-500', text: 'text-white' };
    }
  };
  
  const getZoneLabel = (zone) => {
    switch (zone) {
      case 'green':
        return { label: 'Green Zone', bg: 'bg-green-100', text: 'text-green-800' };
      case 'yellow':
        return { label: 'Yellow Zone', bg: 'bg-yellow-100', text: 'text-yellow-800' };
      case 'red':
        return { label: 'Red Zone', bg: 'bg-red-100', text: 'text-red-800' };
      default:
        return { label: 'Not Available', bg: 'bg-gray-100', text: 'text-gray-800' };
    }
  };
  
  const quadrant = getQuadrantLabel(readiness.quadrant);
  const wealthZone = getZoneLabel(wealthGap.zone);
  const assessmentDate = reportData.assessment_date ? new Date(reportData.assessment_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A';
  
  return (
    <div className="min-h-screen bg-succession-bg-primary text-succession-text-primary py-8 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="bg-succession-bg-secondary border border-succession-border rounded-lg shadow-lg p-6 mb-6">
          <h1 className="text-3xl font-bold text-succession-text-primary mb-2">Succession Readiness Assessment Report</h1>
          <p className="text-succession-text-secondary">
            <strong>Advisor:</strong> {inventoryUser.name || inventoryUser.email}
          </p>
          <p className="text-succession-text-secondary">
            <strong>Assessment Date:</strong> {assessmentDate}
          </p>
        </div>
        
        {/* Readiness Score Section */}
        {readiness.readiness_total != null && readiness.attractiveness_total != null && (
          <div className="bg-succession-bg-secondary border border-succession-border rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-semibold text-succession-text-primary mb-4">Owner Readiness & Business Attractiveness</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="p-4 rounded-lg border border-succession-border bg-succession-bg-primary">
                <div className="text-sm text-succession-text-secondary mb-1">Owner Readiness</div>
                <div className="text-4xl font-bold text-succession-text-primary">{readiness.readiness_total || 0} / 36</div>
              </div>
              <div className="p-4 rounded-lg border border-succession-border bg-succession-bg-primary">
                <div className="text-sm text-succession-text-secondary mb-1">Business Attractiveness</div>
                <div className="text-4xl font-bold text-succession-text-primary">{readiness.attractiveness_total || 0} / 36</div>
              </div>
            </div>
            
            <div className="mt-4">
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${quadrant.bg} ${quadrant.text}`}>
                {quadrant.label}
              </span>
            </div>
          </div>
        )}
        
        {/* Wealth Gap Section */}
        {wealthGap.gap_value != null && (
          <div className="bg-succession-bg-secondary border border-succession-border rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-semibold text-succession-text-primary mb-4">Wealth Gap Analysis</h2>
            
            <div className="space-y-4">
              {wealthGap.goal_type && (
                <div className="p-4 rounded-lg border border-succession-border bg-succession-bg-primary">
                  <div className="text-sm text-succession-text-secondary mb-1">Goal Type</div>
                  <div className="text-lg font-semibold text-succession-text-primary capitalize">
                    {wealthGap.goal_type === 'lump_sum' ? 'Lump Sum Wealth Goal' : 'Annual Income Target'}
                  </div>
                </div>
              )}
              
              {wealthGap.goal_lump_sum != null && wealthGap.goal_type === 'lump_sum' && (
                <div className="p-4 rounded-lg border border-succession-border bg-succession-bg-primary">
                  <div className="text-sm text-succession-text-secondary mb-1">Target Lump Sum</div>
                  <div className="text-2xl font-bold text-succession-text-primary">{formatCurrency(wealthGap.goal_lump_sum)}</div>
                </div>
              )}
              
              {wealthGap.goal_annual_income != null && wealthGap.goal_type === 'income' && (
                <div className="p-4 rounded-lg border border-succession-border bg-succession-bg-primary">
                  <div className="text-sm text-succession-text-secondary mb-1">Target Annual Income</div>
                  <div className="text-2xl font-bold text-succession-text-primary">{formatCurrency(wealthGap.goal_annual_income)}</div>
                </div>
              )}
              
              {wealthGap.current_assets != null && (
                <div className="p-4 rounded-lg border border-succession-border bg-succession-bg-primary">
                  <div className="text-sm text-succession-text-secondary mb-1">Current Investable Assets</div>
                  <div className="text-2xl font-bold text-succession-text-primary">{formatCurrency(wealthGap.current_assets)}</div>
                </div>
              )}
              
              <div className="p-4 rounded-lg border border-succession-border bg-succession-bg-primary">
                <div className="text-sm text-succession-text-secondary mb-1">Wealth Gap</div>
                <div className="text-2xl font-bold text-succession-text-primary">{formatCurrency(wealthGap.gap_value)}</div>
              </div>
              
              {wealthGap.percent_to_goal != null && (
                <div className="p-4 rounded-lg border border-succession-border bg-succession-bg-primary">
                  <div className="text-sm text-succession-text-secondary mb-1">Progress to Goal</div>
                  <div className="text-2xl font-bold text-succession-text-primary">{formatPercent(wealthGap.percent_to_goal)}</div>
                </div>
              )}
              
              {wealthGap.zone && (
                <div className="mt-4">
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold ${wealthZone.bg} ${wealthZone.text}`}>
                    {wealthZone.label}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Profit Gap Section */}
        {profitGap.recasted_ebitda != null && (
          <div className="bg-succession-bg-secondary border border-succession-border rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-semibold text-succession-text-primary mb-4">Normalized EBITDA</h2>
            <div className="p-4 rounded-lg border border-succession-border bg-succession-bg-primary">
              <div className="text-sm text-succession-text-secondary mb-1">Recasted EBITDA</div>
              <div className="text-3xl font-bold text-succession-text-primary">{formatCurrency(profitGap.recasted_ebitda)}</div>
            </div>
          </div>
        )}
        
        {/* Recommended Tasks Section */}
        {tasks.length > 0 && (
          <div className="bg-succession-bg-secondary border border-succession-border rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-2xl font-semibold text-succession-text-primary mb-4">Recommended Next Steps</h2>
            <p className="text-succession-text-secondary mb-4">
              Based on your assessment, here are {tasks.length} recommended tasks to improve your succession readiness:
            </p>
            
            <div className="space-y-3">
              {tasks.map((task, index) => (
                <div key={index} className="p-4 rounded-lg border border-succession-border bg-succession-bg-primary">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-succession-primary text-white flex items-center justify-center font-semibold">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-succession-text-primary mb-1">{task.title || 'Task'}</h3>
                      {task.description && (
                        <p className="text-sm text-succession-text-secondary">{task.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {props?.current_user?.id && (
              <div className="mt-6">
                <a
                  href="/succession/readiness/action-plan"
                  className="inline-block px-6 py-3 bg-succession-primary text-white rounded-lg font-semibold hover:bg-succession-primary-hover transition-colors"
                >
                  Start Working on Tasks
                </a>
              </div>
            )}
          </div>
        )}
        
        {/* Back Button */}
        <div className="mt-6">
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 border border-succession-border rounded-lg text-succession-text-primary hover:bg-succession-bg-secondary transition-colors"
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsultantAssessmentReport;

